import {
  GDSWrapper,
  Inner,
  LogoutModal,
  LogoutNav,
  LogoutNavIcon,
  LogoutNavText,
  MenuItemBadge,
  MenuItemIcon,
  NavigationInner,
  NavigationToggleButton,
  NavigationWrapper,
  NavLink,
  NavLinkInner,
  ResizeHandle,
  SecretButton,
  Wrapper,
  MIN_NAVIGATION_WIDTH,
  MAX_NAVIGATION_WIDTH,
} from "./SideNavigation.styles";
import {
  ArrowIndicatorRightOutlined,
  ArrowIndicatorLeftOutlined,
  SearchOutlined,
  HouseOutlined,
  ShoppingCardOutlined,
  UserFilled,
  GlobeOutlined,
  MoonOutlined,
  LockOutlined,
  FlameOutlined,
  LockFilled,
} from "@gds-react/icons";
import { Icon, Navigation, NavigationItem } from "@gds-react/core";
import { routes } from "../../utils/routeHelper";
import { matchPath, useLocation, useNavigate } from "react-router";
import { Tooltip, TooltipTrigger } from "@gds-react/core";
import useCoreContext from "@gf-core/widgets/hooks/useCoreContext";
import { CoreNavigationTypeEnum } from "@gf-core/widgets/types/CoreNavigationTypeEnum";
import CoreNavigation from "@gf-core/widgets/widgets/CoreNavigation";
import { CoreNavigationRevealItem } from "@gf-core/widgets/types/CoreNavigationRevealItem";
import { useStore } from "../../store/store";
import { useTranslation } from "react-i18next";
import ConnectionStatePostfix from "../ConnectionStatePostfix";
import { Popover } from "react-tiny-popover";
import { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { getEmoji } from "language-flag-colors";
import { useMsal } from "@azure/msal-react";
import SwitchLanguageModal from "../SwitchLanguageModal";
import {
  isLinkingAuthConfigured,
  startLinkAccountRedirect,
} from "../../utils/auth";
import SVGImage from "../SVGImage";
import { ActiveAlertCountWidget } from "@gf-alerting/widgets";
import { DeviceTabTypeEnum } from "../../types/DeviceTabTypeEnum";
import { AccountTabTypeEnum } from "../../types/AccountTabTypeEnum";

export default function SideNavigation() {
  const { t } = useTranslation();
  const showMenu = useStore.useShowMenu();
  const setShowMenu = useStore.useSetShowMenu();
  const showAppStoreFeature = useStore.useShowAppStoreFeature();
  const setShowAppStoreFeature = useStore.useSetShowAppStoreFeature();
  const activeMenuid = useStore.useActiveMenuId();
  const { pathname } = useLocation();
  const { themeConfig } = useCoreContext();
  const [open, setOpen] = useState(false);
  const language = useStore.useLanguageSettings();
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const navigate = useNavigate();
  const setTheme = useStore.useSetTheme();
  const setLoginHint = useStore.useSetLoginHint();
  const { instance } = useMsal();

  const navigationWidth = useStore.useNavigationWidth();
  const setNavigationWidth = useStore.useSetNavigationWidth();
  const navigationRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  const startResize = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMove = (e: PointerEvent) => {
      const left = navigationRef.current?.getBoundingClientRect().left ?? 0;
      const next = Math.min(
        MAX_NAVIGATION_WIDTH,
        Math.max(MIN_NAVIGATION_WIDTH, e.clientX - left),
      );
      setNavigationWidth(next);
    };
    const stop = () => setIsResizing(false);

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", stop);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", stop);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isResizing, setNavigationWidth]);

  const flagEmoji = useMemo(() => {
    return getEmoji(language.iso639Code) ?? getEmoji(language.languageName);
  }, [language]);

  const onLogout = () => {
    setLoginHint("");
    instance.logout();
  };

  const onLinkAccount = async () => {
    setOpen(false);
    await startLinkAccountRedirect();
  };

  const rootLocation = useMemo(() => {
    if (pathname.includes(routes.dashboard.get())) {
      return routes.dashboard.get();
    }
    if (pathname.includes(routes.store.get())) {
      return routes.store.get();
    }
    if (pathname.includes(routes.search.get())) {
      return routes.search.get();
    }
    return "";
  }, [pathname]);

  const revealNavigationItem = useMemo<
    CoreNavigationRevealItem | undefined
  >(() => {
    const device = matchPath(
      { path: routes.device.get(), end: false },
      pathname,
    );
    if (device?.params.accountId) {
      return {
        accountId: device.params.accountId,
        installationId: device.params.installationId,
        deviceTwinId: device.params.deviceId,
      };
    }

    const installation = matchPath(
      { path: routes.installation.get(), end: false },
      pathname,
    );
    if (installation?.params.accountId) {
      return {
        accountId: installation.params.accountId,
        installationId: installation.params.installationId,
      };
    }

    const account = matchPath(
      { path: routes.account.get(), end: false },
      pathname,
    );
    if (account?.params.accountId) {
      return { accountId: account.params.accountId };
    }

    return undefined;
  }, [pathname]);

  const renderPopupContent = () => {
    return (
      <LogoutModal>
        <LogoutNav
          onClick={() => {
            setShowLanguagePicker(true);
          }}
        >
          <LogoutNavIcon>
            {flagEmoji ? (
              flagEmoji
            ) : (
              <GlobeOutlined style={{ width: 20, height: 20 }} />
            )}
          </LogoutNavIcon>
          <LogoutNavText>{t("profileMenu.changeLanguage")}</LogoutNavText>
        </LogoutNav>
        <LogoutNav
          onClick={() => {
            setTheme("light");
          }}
        >
          <LogoutNavIcon>
            <FlameOutlined style={{ width: 20, height: 20 }} />
          </LogoutNavIcon>
          <LogoutNavText>{t("profileMenu.light")}</LogoutNavText>
        </LogoutNav>
        <LogoutNav
          onClick={() => {
            setTheme("dark");
          }}
        >
          <LogoutNavIcon>
            <MoonOutlined style={{ width: 20, height: 20 }} />
          </LogoutNavIcon>
          <LogoutNavText>{t("profileMenu.dark")}</LogoutNavText>
        </LogoutNav>
        {isLinkingAuthConfigured && (
          <LogoutNav
            onClick={() => {
              void onLinkAccount();
            }}
          >
            <LogoutNavIcon>
              <LockFilled style={{ width: 20, height: 20 }} />
            </LogoutNavIcon>
            <LogoutNavText>Connect Wibutler</LogoutNavText>
          </LogoutNav>
        )}
        <LogoutNav onClick={onLogout}>
          <LogoutNavIcon>
            <LockOutlined style={{ width: 20, height: 20 }} />
          </LogoutNavIcon>
          <LogoutNavText>{t("profileMenu.logout")}</LogoutNavText>
        </LogoutNav>
        <SecretButton
          onClick={() => setShowAppStoreFeature(!showAppStoreFeature)}
        />
      </LogoutModal>
    );
  };

  return (
    <Wrapper>
      <GDSWrapper>
        <Navigation
          value={rootLocation}
          onValueChange={(value) => {
            navigate(value);
          }}
          primaryItems={
            <>
              <NavigationItem
                value={routes.dashboard.get()}
                icon={<Icon src={<HouseOutlined />} />}
              >
                {t("sideNavigation.deviceDetails.tooltip")}
              </NavigationItem>
              {showAppStoreFeature && (
                <NavigationItem
                  value={routes.store.get()}
                  icon={<Icon src={<ShoppingCardOutlined />} />}
                >
                  {t("sideNavigation.dashboard.tooltip")}
                </NavigationItem>
              )}

              <NavigationItem
                value={routes.search.get()}
                icon={<Icon src={<SearchOutlined />} />}
              >
                {t("sideNavigation.searchDevices.tooltip")}
              </NavigationItem>
            </>
          }
          secondaryItems={
            <>
              <Popover
                isOpen={open}
                positions={["bottom", "right"]}
                align="center"
                containerStyle={{ padding: "8px", zIndex: "1000" }}
                content={renderPopupContent()}
                onClickOutside={() => setOpen(false)}
              >
                <NavigationItem
                  value="label-6"
                  icon={<Icon src={<UserFilled />} />}
                  onClick={() => setOpen(true)}
                >
                  Profile
                </NavigationItem>
              </Popover>
            </>
          }
        />
      </GDSWrapper>
      <Inner>
        <TooltipTrigger side="right">
          <NavLink to={routes.dashboard.get()}>
            <NavLinkInner $active={pathname.includes("accounts")}>
              <HouseOutlined style={{ width: 20, height: 20 }} />
            </NavLinkInner>
          </NavLink>
          <Tooltip>{t("sideNavigation.deviceDetails.tooltip")}</Tooltip>
        </TooltipTrigger>
        {showAppStoreFeature && (
          <TooltipTrigger side="right">
            <NavLink to={routes.store.get()}>
              <NavLinkInner $active={pathname.includes(routes.store.get())}>
                <ShoppingCardOutlined style={{ width: 20, height: 20 }} />
              </NavLinkInner>
            </NavLink>
            <Tooltip>{t("sideNavigation.dashboard.tooltip")}</Tooltip>
          </TooltipTrigger>
        )}

        <TooltipTrigger side="right">
          <NavLink to={routes.search.get()}>
            <NavLinkInner $active={pathname.includes(routes.search.get())}>
              <SearchOutlined style={{ width: 20, height: 20 }} />
            </NavLinkInner>
          </NavLink>
          <Tooltip>{t("sideNavigation.searchDevices.tooltip")}</Tooltip>
        </TooltipTrigger>

        <SecretButton
          onClick={() => setShowAppStoreFeature(!showAppStoreFeature)}
        />
      </Inner>
      {!pathname.includes("search") && !pathname.includes("store") && (
        <NavigationWrapper
          ref={navigationRef}
          $appearance={themeConfig.appearance}
          $show={showMenu}
          $width={navigationWidth}
          $resizing={isResizing}
        >
          <NavigationInner $show={showMenu} $width={navigationWidth}>
            <CoreNavigation
              id="side-navigation"
              activeNavigationItemId={activeMenuid}
              pageSize={16}
              termsAndConditionsUrl={
                import.meta.env.VITE_TERMS_AND_CONDITIONS_URL
              }
              revealNavigationItem={revealNavigationItem}
              activeNavigationLevels={[
                CoreNavigationTypeEnum.ACCOUNT,
                CoreNavigationTypeEnum.SUBACCOUNT,
                CoreNavigationTypeEnum.ZONE,
                CoreNavigationTypeEnum.INSTALLATION,
                CoreNavigationTypeEnum.GATEWAY,
                CoreNavigationTypeEnum.DEVICE,
                CoreNavigationTypeEnum.LOCATION,
              ]}
              isClickableNavigationItem={(type) => {
                if (
                  type === "account" ||
                  type === "installation" ||
                  type === "gateway" ||
                  type === "device"
                ) {
                  return true;
                } else {
                  return false;
                }
              }}
              onNavigationItemClicked={(event) => {
                const { type, accountId, parentId, id } = event;
                if (type === CoreNavigationTypeEnum.INSTALLATION) {
                  navigate(routes.installation.set(accountId, id));
                }

                if (type === CoreNavigationTypeEnum.ACCOUNT) {
                  navigate(routes.account.set(accountId));
                }

                if (
                  (type === CoreNavigationTypeEnum.GATEWAY ||
                    type === CoreNavigationTypeEnum.DEVICE) &&
                  parentId
                ) {
                  navigate(routes.device.set(accountId, parentId, id));
                }
              }}
              postfix={(type, accountId, installationId, deviceId) => {
                if (type === CoreNavigationTypeEnum.ACCOUNT && accountId) {
                  return (
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        navigate(
                          routes.account.set(
                            accountId,
                            AccountTabTypeEnum.ActiveAlarms,
                          ),
                        );
                      }}
                    >
                      <ActiveAlertCountWidget
                        sourceId={accountId}
                        variant="bell"
                        hideWhenEmpty
                      />
                    </span>
                  );
                }

                if (
                  (type === CoreNavigationTypeEnum.GATEWAY ||
                    type === CoreNavigationTypeEnum.DEVICE) &&
                  installationId &&
                  deviceId
                ) {
                  return (
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        navigate(
                          routes.device.set(
                            accountId,
                            installationId,
                            deviceId,
                            DeviceTabTypeEnum.ActiveAlerts,
                          ),
                        );
                      }}
                    >
                      <ActiveAlertCountWidget
                        sourceId={deviceId}
                        variant="bell"
                        hideWhenEmpty
                      />
                    </span>
                  );
                }

                return undefined;
              }}
              prefix={(type, _, installationId, deviceId) => {
                if (
                  type == CoreNavigationTypeEnum.GATEWAY &&
                  installationId &&
                  deviceId
                ) {
                  return (
                    <MenuItemIcon>
                      <MenuItemBadge>
                        <ConnectionStatePostfix
                          deviceId={deviceId}
                          installationId={installationId}
                        />
                      </MenuItemBadge>
                      <SVGImage
                        icon="CloudGatewaySVG"
                        style={{
                          width: 14,
                          height: 14,
                          filter:
                            themeConfig.appearance === "dark"
                              ? "invert(1)"
                              : "invert(0)",
                        }}
                      />
                    </MenuItemIcon>
                  );
                }

                return undefined;
              }}
            />
          </NavigationInner>
          {showMenu && (
            <ResizeHandle onPointerDown={startResize} $resizing={isResizing} />
          )}
          <NavigationToggleButton
            $show={showMenu}
            onClick={() => setShowMenu(!showMenu)}
          >
            {showMenu ? (
              <ArrowIndicatorLeftOutlined />
            ) : (
              <ArrowIndicatorRightOutlined />
            )}
          </NavigationToggleButton>
        </NavigationWrapper>
      )}
      <SwitchLanguageModal
        show={showLanguagePicker}
        onClose={() => setShowLanguagePicker(false)}
      />
    </Wrapper>
  );
}
